import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import PageLayout from '../../layouts/PageLayout';
import PaginationComponent from '../../components/PaginationComponent';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';
import { useBranch } from '../../context/BranchContext';
import { customerPhoneViewLogAPI, employeeAPI } from '../../services';

/**
 * #232308 — NHẬT KÝ XEM SĐT KHÁCH (lệnh sếp 02/09/2026): *"nhân viên nào bấm xem số
 * khách nào thì tôi phải biết chứ không phải ai xem cũng như ai"*.
 *
 * Làm cho ĐIỆN THOẠI TRƯỚC (luật 31/08): khổ hẹp dùng danh sách thẻ, bảng chỉ bật từ
 * `lg` trở lên — bảng 8 cột ở 390px sẽ tràn ngang, không đọc được.
 */

const TIMEZONE = 'Asia/Ho_Chi_Minh';
const DEFAULT_PRESET = 'last7days';
const LIMIT = 20;

const SUBJECT_LABELS = {
  ORDER: 'Đơn hàng',
  RESERVATION: 'Đặt bàn',
  RESERVATION_LIST: 'Danh sách',
};
const SUBJECT_VARIANTS = { ORDER: 'primary', RESERVATION: 'info', RESERVATION_LIST: 'secondary' };

/**
 * Dòng dạng DANH SÁCH không quy về một khách nào: một lượt mở màn danh sách phơi ra
 * nhiều số cùng lúc. Hiện "N số khách" thay cho ô SĐT để không ai đọc nhầm là nhân
 * viên đã bấm xem đúng một khách.
 */
const laDongDanhSach = (row) => row.subject_type === 'RESERVATION_LIST';
const moTaSo = (row) =>
  laDongDanhSach(row) ? `${row.exposed_count ?? 0} số khách` : row.customer_phone || '—';
const ROLE_LABELS = { ADMIN: 'Quản trị', MANAGER: 'Quản lý', STAFF: 'Nhân viên' };

const fmt = (dt) => (dt ? moment.tz(dt, TIMEZONE).format('DD/MM/YYYY HH:mm') : '—');

export default function CustomerPhoneViewLogPage() {
  // Chi nhánh chọn ở thanh trên đầu (#175019), trang không có ô lọc riêng.
  const { selectedBranch } = useBranch();
  const branchFilter = selectedBranch?.id || '';

  const initDates = calcDatePreset(DEFAULT_PRESET);
  const [fromDate, setFromDate] = useState(initDates.from);
  const [toDate, setToDate] = useState(initDates.to);
  const [actorId, setActorId] = useState('');
  const [subjectType, setSubjectType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [health, setHealth] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (branchFilter) params.branch_id = branchFilter;
      if (actorId) params.actor_id = actorId;
      if (subjectType) params.subject_type = subjectType;
      if (q) params.q = q;

      // apiClient trả thẳng body {data, meta} — KHÔNG bọc thêm một lớp `data` nữa.
      const res = await customerPhoneViewLogAPI.getLogs(params);
      setData(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta ?? null);
    } catch {
      toast.error('Không tải được nhật ký xem SĐT');
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate, branchFilter, actorId, subjectType, q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    employeeAPI
      .getEmployees({ limit: 200, branch_id: branchFilter || undefined })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setEmployees(list);
      })
      .catch(() => setEmployees([]));
  }, [branchFilter]);

  useEffect(() => {
    customerPhoneViewLogAPI
      .getHealth()
      .then((res) => setHealth(res ?? null))
      .catch(() => setHealth(null));
  }, []);

  const resetPage = (fn) => (value) => { fn(value); setPage(1); };
  const onDateChange = (from, to) => { setFromDate(from); setToDate(to); setPage(1); };
  const submitSearch = (e) => { e.preventDefault(); setQ(searchInput.trim()); setPage(1); };

  const totalViews = useMemo(
    () => data.reduce((sum, r) => sum + (r.view_count || 1), 0),
    [data],
  );

  const renderActor = (row) => (
    <>
      <div className="fw-semibold">{row.actor_name || row.actor_id}</div>
      <div className="text-muted small">{ROLE_LABELS[row.actor_role] || row.actor_role || '—'}</div>
    </>
  );

  return (
    <PageLayout title="Nhật ký xem SĐT khách">
      {health && !health.healthy && (
        <Alert variant="danger" className="py-2">
          <strong>Nhật ký đang GHI LỖI</strong> — {health.consecutive_failures} lần liên tiếp
          {health.last_error ? `: ${health.last_error}` : ''}. Số liệu dưới đây có thể thiếu, báo kỹ thuật ngay.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body className="py-2">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onChange={onDateChange}
            defaultPreset={DEFAULT_PRESET}
          />
          <Row className="g-2 mt-1">
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={actorId}
                onChange={(e) => resetPage(setActorId)(e.target.value)}
                aria-label="Lọc theo nhân viên"
              >
                <option value="">Tất cả nhân viên</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name || emp.username}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Select
                size="sm"
                value={subjectType}
                onChange={(e) => resetPage(setSubjectType)(e.target.value)}
                aria-label="Lọc theo nơi xem"
              >
                <option value="">Tất cả nơi xem</option>
                <option value="ORDER">Chi tiết đơn hàng</option>
                <option value="RESERVATION">Chi tiết đặt bàn</option>
                <option value="RESERVATION_LIST">Tải danh sách đặt bàn</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={5}>
              <Form onSubmit={submitSearch} className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="SĐT, tên khách, số đơn hoặc tên nhân viên"
                  aria-label="Tìm theo SĐT hoặc tên khách"
                />
                <Button size="sm" type="submit" variant="primary">Tìm</Button>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
        <span>{meta ? `${meta.total} lượt xem` : '—'}</span>
        <span>{totalViews} lần mở trong trang này</span>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : data.length === 0 ? (
        <Card body className="text-center text-muted">Chưa có lượt xem SĐT nào trong khoảng đã chọn.</Card>
      ) : (
        <>
          {/* Điện thoại: danh sách thẻ */}
          <div className="d-lg-none">
            {data.map((row) => (
              <Card key={row.id} className="mb-2">
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>{renderActor(row)}</div>
                    <Badge bg={SUBJECT_VARIANTS[row.subject_type] || 'secondary'}>
                      {SUBJECT_LABELS[row.subject_type] || row.subject_type}
                    </Badge>
                  </div>
                  <div className="mt-2">
                    <div className="fw-bold fs-6">{moTaSo(row)}</div>
                    <div className="text-muted small">
                      {laDongDanhSach(row)
                        ? 'Mở màn danh sách — không nhắm vào một khách'
                        : row.customer_name || 'Khách chưa có tên'}
                    </div>
                  </div>
                  <div className="text-muted small mt-2">
                    {row.subject_ref ? `${row.subject_ref} · ` : ''}
                    {row.branch_name || '—'}
                  </div>
                  <div className="d-flex justify-content-between small mt-1">
                    <span>{fmt(row.last_viewed_at)}</span>
                    <span>{row.view_count > 1 ? `${row.view_count} lần` : '1 lần'}</span>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* Máy tính: bảng đầy đủ */}
          <div className="d-none d-lg-block">
            <Card>
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Thời điểm</th>
                    <th>Nhân viên</th>
                    <th>SĐT khách</th>
                    <th>Khách</th>
                    <th>Nơi xem</th>
                    <th>Chứng từ</th>
                    <th>Chi nhánh</th>
                    <th className="text-end">Số lần</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.id}>
                      <td>{fmt(row.last_viewed_at)}</td>
                      <td>{renderActor(row)}</td>
                      <td className="fw-bold">{moTaSo(row)}</td>
                      <td>
                        {laDongDanhSach(row) ? (
                          <span className="text-muted small">Mở màn danh sách</span>
                        ) : (
                          row.customer_name || '—'
                        )}
                      </td>
                      <td>
                        <Badge bg={SUBJECT_VARIANTS[row.subject_type] || 'secondary'}>
                          {SUBJECT_LABELS[row.subject_type] || row.subject_type}
                        </Badge>
                      </td>
                      <td>{row.subject_ref || '—'}</td>
                      <td>{row.branch_name || '—'}</td>
                      <td className="text-end">{row.view_count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </div>

          {meta && meta.total_pages > 1 && (
            <div className="mt-3">
              <PaginationComponent
                totalItems={meta.total}
                itemsPerPage={LIMIT}
                currentPage={page}
                onPageChange={setPage}
                loading={loading}
              />
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
}
